import { TherapyRelationshipType } from "~/prisma/browser";

/**
 * SHARED booking cart types and utilities
 *
 * All cookie operations now go through Server Actions to avoid document.cookie usage.
 * This provides a cleaner, more Next.js-native approach where:
 * - Client components call server actions for cookie operations
 * - Server components use server utilities directly
 * - All cookie management is centralized through Next.js cookies() API
 */

export interface BookingCartItem {
  // Essential booking information
  therapistUserId: string;
  therapistSlug: string;
  selectedDateTime: string; // ISO string
  therapyType: TherapyRelationshipType;

  // URL to return to booking page
  bookingUrl: string;

  // Additional context
  therapistName?: string;
  therapistImage?: string;
  currentStep?: number;
  timezone: string;
  minimumBookingLeadTime?: number;

  // Metadata
  createdAt: string;
  expiresAt: string; // 14 days from creation
}

/**
 * Builds the booking resume URL.
 *
 * Pass baseUrl when the caller needs an absolute URL. Without baseUrl this
 * helper intentionally treats bookingUrl as app-relative and returns only the
 * path, query, and hash.
 */
export function buildBookingReturnUrl(
  item: Pick<
    BookingCartItem,
    | "bookingUrl"
    | "selectedDateTime"
    | "therapyType"
    | "minimumBookingLeadTime"
  >,
  baseUrl?: string,
  now = Date.now(),
) {
  const url = new URL(item.bookingUrl, baseUrl ?? "http://localhost:3000");

  const canKeepDate =
    new Date(item.selectedDateTime).getTime() >
      now + (item.minimumBookingLeadTime ?? 0) * 3600000;
  if (canKeepDate) url.searchParams.set("date", item.selectedDateTime);
  else url.searchParams.delete("date");
  url.searchParams.set("step", canKeepDate ? "1" : "0");

  if (item.therapyType !== TherapyRelationshipType.INDIVIDUAL) {
    url.searchParams.set("therapyType", item.therapyType);
  } else {
    url.searchParams.delete("therapyType");
  }

  if (baseUrl) return url.toString();

  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * CLIENT-SIDE booking cart utilities that use Server Actions
 *
 * These functions are designed to be called from client components and will
 * internally use Server Actions to perform cookie operations safely.
 */

/**
 * Utility to create FormData for server actions
 */
export const createBookingFormData = (
  item: Omit<BookingCartItem, "createdAt" | "expiresAt">,
): FormData => {
  const formData = new FormData();
  formData.append("therapistUserId", item.therapistUserId);
  formData.append("therapistSlug", item.therapistSlug);
  formData.append("selectedDateTime", item.selectedDateTime);
  formData.append("therapyType", item.therapyType);
  formData.append("bookingUrl", item.bookingUrl);
  formData.append("timezone", item.timezone);

  if (item.therapistName) {
    formData.append("therapistName", item.therapistName);
  }

  if (item.therapistImage) {
    formData.append("therapistImage", item.therapistImage);
  }

  if (item.currentStep !== undefined) {
    formData.append("currentStep", item.currentStep.toString());
  }

  return formData;
};

/**
 * Get booking cart summary for display - server-safe version
 */
export const getBookingCartSummary = (cartItem: BookingCartItem | null) => {
  if (!cartItem) return null;

  const selectedDate = new Date(cartItem.selectedDateTime);
  const therapyTypeLabel =
    cartItem.therapyType === "INDIVIDUAL"
      ? "Individualna"
      : cartItem.therapyType === "COUPLES"
        ? "Za parove"
        : "Grupna";

  return {
    therapistName: cartItem.therapistName || "Nepoznat terapeut",
    therapistImage: cartItem.therapistImage,
    selectedDate,
    therapyType: therapyTypeLabel,
    currentStep: cartItem.currentStep || 0,
    isExpired: new Date() > new Date(cartItem.expiresAt),
  };
};
